"use client";

import Image from "next/image";
import ScrollReveal from "@/components/common/ScrollReveal";
import BorderedHeading from "@/components/common/BorderedHeading";
import Table, { Column } from "@/components/ui/Table";

interface Achievement {
  year: string;
  program: string;
  team: string;
  rank: string;
}

const certificates = [
  { name: "OSCE³", image: "/images/Company/OSCE.svg" },
  { name: "OSEP", image: "/images/Company/OSEP.svg" },
  { name: "OSED", image: "/images/Company/OSED.svg" },
  { name: "OSWE", image: "/images/Company/OSWE.svg" },
  { name: "OSCP", image: "/images/Company/OSCP.svg" },
  { name: "OSWP", image: "/images/Company/OSWP.svg" },
  { name: "GPEN", image: "/images/Company/GPEN.svg" },
  { name: "GWAPT", image: "/images/Company/GWAPT.svg" },
  { name: "GXPN", image: "/images/Company/GXPN.svg" },
  { name: "GRID", image: "/images/Company/GRID.svg" },
  { name: "GSEC", image: "/images/Company/GSEC.svg" },
  { name: "GREM", image: "/images/Company/GREM.svg" },
  { name: "CREST", image: "/images/Company/Crest.svg" },
  { name: "CEH", image: "/images/Company/CEH.svg" },
  { name: "CISSP", image: "/images/Company/CISSP.svg" },
  { name: "RED TEAM", image: "/images/Company/RED_TEAM.svg" },
  { name: "CCNP", image: "/images/Company/CCNP.svg" },
  { name: "CCNA", image: "/images/Company/CCNA.svg" },
];

// Newest first. `team: "N/A"` is the existing stand-in for results that are not
// attributed to a named team; `rank` carries a word instead of a placing where
// the achievement has no placing (see "Completed" / "Recipient").
const achievements: Achievement[] = [
  {
    year: "2025",
    program: "XCTF Final",
    team: "N/A",
    rank: "1st",
  },
  {
    year: "2024",
    program: "KISA Asean CTF",
    team: "Vietnam team",
    rank: "1st",
  },
  {
    year: "2024",
    program: "GCC Thailand CTF",
    team: "Vietnam team",
    rank: "1st",
  },
  {
    year: "2024",
    program: "Microsoft Top 10 Security Researcher – Dynamics",
    team: "N/A",
    rank: "6th (Global)",
  },
  {
    year: "2023",
    program: "Pwn2Own Miami ICS/SCADA",
    team: "Vietnam team",
    rank: "2nd",
  },
  {
    year: "2023",
    program: "GCC Singapore CTF",
    team: "Vietnam team",
    rank: "1st",
  },
  {
    year: "2019",
    program: "Cyber Security Agency (Singapore) Innovation Award – up to $500k SGD",
    team: "N/A",
    rank: "Recipient",
  },
  {
    year: "2019",
    program: "Flare On Challenge by Fireeye",
    team: "lum6l",
    rank: "Completed",
  },
  {
    year: "2018",
    program: "Cyber Seagame",
    team: "L0t3r",
    rank: "3rd",
  },
  {
    year: "2018",
    program: "Students contest on Information Security",
    team: "UIT+KOX_r3d0m",
    rank: "3rd",
  },
  {
    year: "2018",
    program: "Singapore Cyber Conquest",
    team: "Vietnam-UIT.MeePwn",
    rank: "2nd",
  },
  {
    year: "2018",
    program: "VietTel MasterCTF\n(2 teams)",
    team: "L0t3r\npwntus1",
    rank: "1st\n2nd",
  },
  {
    year: "2017",
    program: "Hacker One | HackTheWorld competition in 2017\nMost Value Player (MVP) Award 2017",
    team: "BiBo",
    rank: "22nd",
  },
  {
    year: "2017",
    program: "International Olympiad, NGOCrypto",
    team: "N/A",
    rank: "3rd",
  },
  {
    year: "2017",
    program: "Students contest on Information Security",
    team: "UIT-j3dL zone@UIT",
    rank: "1st\n3rd",
  },
  {
    year: "2017",
    program: "PwC Vietnam Hackaday\n(2 teams)",
    team: "L0t3r\n20j09",
    rank: "1st\n3rd",
  },
  {
    year: "2017",
    program: "HITB GSEC CTF",
    team: "N/A",
    rank: "8th",
  },
  {
    year: "2017",
    program: "WhiteHat Grandprix",
    team: "CLGT#MeePwn",
    rank: "1st",
  },
  {
    year: "2016",
    program: "Students contest on Information Security",
    team: "UIT-j3dL",
    rank: "2nd",
  },
  {
    year: "2016",
    program: "FacebookCTF (HITB GSEC) in Singapore",
    team: "pwnie",
    rank: "5th",
  },
  {
    year: "2016",
    program: "International Olympiad in Cryptography 2016 Professional Round",
    team: "N/A",
    rank: "3rd",
  },
  {
    year: "2016",
    program: "i-SECURE CTF",
    team: "N/A",
    rank: "1st",
  },
  {
    year: "2016",
    program: "9th Thailand Network Security Contest 2016",
    team: "pwnladin",
    rank: "1st",
  },
  {
    // Two ECQ teams took the top two places, folded into one row with the same
    // "\n + (2 teams)" convention as the VietTel MasterCTF row above.
    year: "2015",
    program: "Cyber S.E.A. Games\n(2 teams)",
    team: "l4w\npwnladin",
    rank: "1st\n2nd",
  },
  {
    year: "2015",
    program: "Thailand CTF Final Round",
    team: "pwnladin",
    rank: "1st",
  },
  {
    year: "2015",
    program: "Thailand CTF Qualify Round",
    team: "pwnladin",
    rank: "1st",
  },
  {
    year: "2014",
    program: "CDIC Cyber WAR Final Round",
    team: "pwnladin",
    rank: "1st",
  },
  {
    year: "2014",
    program: "CDIC Cyber WAR Qualify Round",
    team: "pwnladin",
    rank: "1st",
  },
  {
    year: "2014",
    program: "National Software Contest",
    team: "N/A",
    rank: "3rd",
  },
  {
    year: "2013",
    program: "8th Thailand Network Security Contest",
    team: "N/A",
    rank: "3rd",
  },
];

const columns: Record<string, Column<Achievement>> = {
  year: {
    id: "year",
    name: "Year",
    show: true,
    sort: false,
    align: "left",
    render: (rowData) => <span className="text-primary font-semibold">{rowData.year}</span>,
  },
  program: {
    id: "program",
    name: "Program",
    show: true,
    sort: false,
    align: "left",
    render: (rowData) => <span className="whitespace-pre-line">{rowData.program}</span>,
  },
  team: {
    id: "team",
    name: "Team",
    show: true,
    sort: false,
    align: "left",
    render: (rowData) => (
      <span className="whitespace-pre-line text-text-highlight">{rowData.team}</span>
    ),
  },
  rank: {
    id: "rank",
    name: "Rank",
    show: true,
    sort: false,
    align: "left",
    render: (rowData) => (
      <span className="whitespace-pre-line font-semibold text-primary">{rowData.rank}</span>
    ),
  },
};

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-4 mb-6">
      <BorderedHeading
        as="h2"
        variant="headline-4"
        className="text-white font-semibold whitespace-nowrap"
      >
        {title}
      </BorderedHeading>
      <div className="flex-1 h-px bg-white/15" />
    </div>
  );
}

export default function Team() {
  return (
    <section className="w-full px-4 sm:px-6 lg:px-8 pt-2 pb-12">
      <div className="w-full max-w-[1300px] mx-auto">
        {/* Certificates */}
        <ScrollReveal direction="up">
          <SectionHeader title="Certificate" />
        </ScrollReveal>
        <ScrollReveal direction="up" delay={80}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 mb-12 md:mb-16">
            {certificates.map((cert) => (
              <div
                key={cert.name}
                className="aspect-square flex items-center justify-center overflow-hidden rounded-xl border-[1.5px] border-[rgba(151,151,151,0.4)]"
              >
                <Image
                  src={cert.image}
                  alt={cert.name}
                  width={160}
                  height={160}
                  className="w-2/3 h-2/3 object-contain"
                  unoptimized
                />
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Achievements */}
        <ScrollReveal direction="up" delay={160}>
          <SectionHeader title="Achievements" />
        </ScrollReveal>
        <ScrollReveal direction="up" delay={240}>
          <Table
            columns={columns}
            data={achievements}
            customClassName="rounded-lg overflow-hidden border border-white/10"
          />
        </ScrollReveal>
      </div>
    </section>
  );
}
